import { API_BASE_URL } from '../config';
import type { FormEmployerContext } from '../components/admin/applicationManagement/applicationHome/createInvitation/invitationEmployerFields';

export type InvitationEditLockReason =
  | 'UPLOADED'
  | 'SUBMITTED'
  | 'IN_PROGRESS'
  | 'COMPLETED';

export interface InvitationEditAssignmentItem {
  code: string;
  nameJa: string;
  selected: boolean;
  locked: boolean;
  lockReason: InvitationEditLockReason | null;
}

export interface InvitationEditRecipient {
  email: string;
  name: string;
  status: string;
}

export interface InvitationEditDto {
  invitationId: string;
  deadline: string;
  editable: boolean;
  editBlockReason: string | null;
  recipientCount: number;
  sharedInvitation: boolean;
  recipients: InvitationEditRecipient[];
  documents: InvitationEditAssignmentItem[];
  forms: InvitationEditAssignmentItem[];
  formEmployerContext: FormEmployerContext;
}

export interface UpdateInvitationRequest {
  deadline: string;
  docCodes: string[];
  formCodes: string[];
  formEmployerContext: FormEmployerContext;
  confirmSharedUpdate?: boolean;
}

export interface SplitInvitationRecipientRequest {
  email: string;
  deadline: string;
  docCodes: string[];
  formCodes: string[];
  formEmployerContext: FormEmployerContext;
  acknowledgeProgressLoss?: boolean;
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    if (body?.error) return body.error;
    if (body?.message) return body.message;
  } catch {
    /* ignore */
  }
  if (res.status === 500) {
    return 'サーバーエラーが発生しました。バックエンドのログを確認してください。';
  }
  return '更新に失敗しました。';
}

export async function fetchInvitationForEdit(
  invitationId: string
): Promise<InvitationEditDto | null> {
  if (!API_BASE_URL || !invitationId?.trim()) return null;
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/invitations/${encodeURIComponent(invitationId.trim())}`
    );
    if (!res.ok) return null;
    return (await res.json()) as InvitationEditDto;
  } catch {
    return null;
  }
}

export async function updateInvitation(
  invitationId: string,
  body: UpdateInvitationRequest
): Promise<{ ok: true; message: string } | { ok: false; error: string }> {
  if (!API_BASE_URL || !invitationId?.trim()) {
    return { ok: false, error: '更新に失敗しました。' };
  }
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/invitations/${encodeURIComponent(invitationId.trim())}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) {
      return { ok: false, error: await parseError(res) };
    }
    const data = (await res.json()) as { message?: string };
    return { ok: true, message: data.message ?? '招待内容を更新しました。' };
  } catch {
    return { ok: false, error: '更新に失敗しました。' };
  }
}

export async function splitInvitationRecipient(
  invitationId: string,
  body: SplitInvitationRecipientRequest
): Promise<
  | { ok: true; newInvitationId: string }
  | { ok: false; error: string }
> {
  if (!API_BASE_URL || !invitationId?.trim()) {
    return { ok: false, error: '分離に失敗しました。' };
  }
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/invitations/${encodeURIComponent(invitationId.trim())}/split-recipient`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) {
      return { ok: false, error: await parseError(res) };
    }
    const data = (await res.json()) as { newInvitationId?: string };
    return { ok: true, newInvitationId: data.newInvitationId ?? '' };
  } catch {
    return { ok: false, error: '分離に失敗しました。' };
  }
}

export function lockReasonLabel(reason: InvitationEditLockReason | null | undefined): string {
  switch (reason) {
    case 'UPLOADED':
      return 'アップロード済み';
    case 'SUBMITTED':
      return '提出済み';
    case 'IN_PROGRESS':
      return '記入・選択済み';
    case 'COMPLETED':
      return '記入完了';
    default:
      return 'ロック中';
  }
}
